/** Layout map size (Interactive4 ImageMap card). */
export const INTERACTIVE4_MAP_W = 400;
export const INTERACTIVE4_MAP_H = 420;

/** Must match learner `btnSize` / editor hotspot outer size (POPUP / AUDIO_POPUP / AudioPoint). */
export const INTERACTIVE4_BTN_SIZE = 64;

/**
 * Fullscreen / large intrinsic images use mapW ≫ phone width: (btn/mapW)*100 becomes tiny in
 * %-points while hotspots use the same %-space as the rendered box — on a narrow viewport a
 * few %-points are still far less than 64px. Floor safe distance using a conservative small
 * viewport so overlap resolution matches what learners actually see (see Interactive3 / ENG-9451).
 */
const MIN_ASSUMED_VIEWPORT_W = 360;
const MIN_ASSUMED_VIEWPORT_H = 640;

function safeDistancePercent(
  mapPx: number,
  minViewportPx: number,
  gap: number
): number {
  const fromMap = (INTERACTIVE4_BTN_SIZE / mapPx) * 100 + gap;
  const fromViewportFloor = (INTERACTIVE4_BTN_SIZE / minViewportPx) * 100 + gap;
  return Math.max(fromMap, fromViewportFloor);
}

/**
 * Same overlap resolution as learner template: nudge anchors in reading order until stable.
 * @param mapW - actual container/image width in pixels (defaults to INTERACTIVE4_MAP_W=400)
 * @param mapH - actual container/image height in pixels (defaults to INTERACTIVE4_MAP_H=420)
 * @see Interactive4 translator
 */
export function resolveInteractive4Overlaps(
  points: Array<{ left: number; top: number }>,
  isMobile: boolean,
  mapW = INTERACTIVE4_MAP_W,
  mapH = INTERACTIVE4_MAP_H
): void {
  if (points.length < 2) return;

  const GAP = 2;
  const safeX = safeDistancePercent(mapW, MIN_ASSUMED_VIEWPORT_W, GAP);
  const safeY = safeDistancePercent(mapH, MIN_ASSUMED_VIEWPORT_H, GAP);

  const halfBtnX = (INTERACTIVE4_BTN_SIZE / 2 / mapW) * 100;
  const halfBtnY = (INTERACTIVE4_BTN_SIZE / 2 / mapH) * 100;
  const bMinX = halfBtnX;
  const bMinY = halfBtnY;
  const bMaxX = isMobile ? 80 : 100 - halfBtnX;
  const bMaxY = isMobile ? 80 : 100 - halfBtnY;

  const clampPt = (p: { left: number; top: number }) => {
    p.left = Math.max(bMinX, Math.min(bMaxX, p.left));
    p.top = Math.max(bMinY, Math.min(bMaxY, p.top));
  };

  const order = points
    .map((_, i) => i)
    .sort(
      (a, b) =>
        points[a].top + points[a].left - (points[b].top + points[b].left)
    );

  for (let pass = 0; pass < 120; pass++) {
    let anyMoved = false;

    for (let k = 1; k < order.length; k++) {
      const idx = order[k];

      let worstOther = -1;
      let worstArea = 0;

      for (let m = 0; m < order.length; m++) {
        if (m === k) continue;
        const o = order[m];
        const dx = Math.abs(points[idx].left - points[o].left);
        const dy = Math.abs(points[idx].top - points[o].top);
        if (dx >= safeX || dy >= safeY) continue;
        const area = (safeX - dx) * (safeY - dy);
        if (area > worstArea) {
          worstArea = area;
          worstOther = o;
        }
      }

      if (worstOther === -1) continue;

      const relX = points[idx].left - points[worstOther].left;
      const relY = points[idx].top - points[worstOther].top;
      const EPS = 0.15;
      const needX = safeX - Math.abs(relX) + EPS;
      const needY = safeY - Math.abs(relY) + EPS;
      const saved = { left: points[idx].left, top: points[idx].top };

      const tryNudge = (dL: number, dT: number): boolean => {
        const nL = saved.left + dL;
        const nT = saved.top + dT;
        if (nL < bMinX || nL > bMaxX || nT < bMinY || nT > bMaxY) return false;
        if (
          Math.abs(nL - points[worstOther].left) < safeX &&
          Math.abs(nT - points[worstOther].top) < safeY
        )
          return false;
        points[idx].left = nL;
        points[idx].top = nT;
        return true;
      };

      const dirX = relX >= 0 ? 1 : -1;
      const dirY = relY >= 0 ? 1 : -1;

      const moved =
        needX <= needY
          ? tryNudge(dirX * needX, 0) ||
            tryNudge(0, dirY * needY) ||
            tryNudge(-dirX * needX, 0) ||
            tryNudge(0, -dirY * needY) ||
            tryNudge(dirX * needX, dirY * needY)
          : tryNudge(0, dirY * needY) ||
            tryNudge(dirX * needX, 0) ||
            tryNudge(0, -dirY * needY) ||
            tryNudge(-dirX * needX, 0) ||
            tryNudge(dirX * needX, dirY * needY);

      if (!moved) {
        points[idx].left = Math.min(bMaxX, saved.left + Math.abs(needX));
        points[idx].top = Math.min(bMaxY, saved.top + Math.abs(needY));
      }

      anyMoved = true;
    }

    if (!anyMoved) break;
  }

  for (const p of points) clampPt(p);
}

/**
 * Editor drag: only the proposed point moves; fixed others stay. Same safe distance as resolveInteractive4Overlaps.
 * @param mapW - actual container/image width in pixels (defaults to INTERACTIVE4_MAP_W=400)
 * @param mapH - actual container/image height in pixels (defaults to INTERACTIVE4_MAP_H=420)
 */
export function nudgeDraggedHotspotAwayFromFixed(
  proposed: { left: number; top: number },
  fixedOthers: ReadonlyArray<{ left: number; top: number }>,
  isMobile: boolean,
  mapW = INTERACTIVE4_MAP_W,
  mapH = INTERACTIVE4_MAP_H
): { left: number; top: number } {
  const p = { ...proposed };
  if (fixedOthers.length === 0) return p;

  const GAP = 2;
  const safeX = safeDistancePercent(mapW, MIN_ASSUMED_VIEWPORT_W, GAP);
  const safeY = safeDistancePercent(mapH, MIN_ASSUMED_VIEWPORT_H, GAP);

  const halfBtnX = (INTERACTIVE4_BTN_SIZE / 2 / mapW) * 100;
  const halfBtnY = (INTERACTIVE4_BTN_SIZE / 2 / mapH) * 100;
  const bMinX = halfBtnX;
  const bMinY = halfBtnY;
  const bMaxX = isMobile ? 80 : 100 - halfBtnX;
  const bMaxY = isMobile ? 80 : 100 - halfBtnY;

  const clampPt = () => {
    p.left = Math.max(bMinX, Math.min(bMaxX, p.left));
    p.top = Math.max(bMinY, Math.min(bMaxY, p.top));
  };

  const clearOfAllFixed = (nL: number, nT: number): boolean => {
    for (let j = 0; j < fixedOthers.length; j++) {
      const fo = fixedOthers[j];
      if (Math.abs(nL - fo.left) < safeX && Math.abs(nT - fo.top) < safeY) {
        return false;
      }
    }
    return true;
  };

  for (let pass = 0; pass < 120; pass++) {
    let worstOther = -1;
    let worstArea = 0;

    for (let m = 0; m < fixedOthers.length; m++) {
      const o = fixedOthers[m];
      const dx = Math.abs(p.left - o.left);
      const dy = Math.abs(p.top - o.top);
      if (dx >= safeX || dy >= safeY) continue;
      const area = (safeX - dx) * (safeY - dy);
      if (area > worstArea) {
        worstArea = area;
        worstOther = m;
      }
    }

    if (worstOther === -1) break;

    const o = fixedOthers[worstOther];
    const relX = p.left - o.left;
    const relY = p.top - o.top;
    const EPS = 0.15;
    const needX = safeX - Math.abs(relX) + EPS;
    const needY = safeY - Math.abs(relY) + EPS;
    const saved = { left: p.left, top: p.top };

    const tryNudge = (dL: number, dT: number): boolean => {
      const nL = saved.left + dL;
      const nT = saved.top + dT;
      if (nL < bMinX || nL > bMaxX || nT < bMinY || nT > bMaxY) return false;
      if (!clearOfAllFixed(nL, nT)) return false;
      p.left = nL;
      p.top = nT;
      return true;
    };

    const dirX = relX >= 0 ? 1 : -1;
    const dirY = relY >= 0 ? 1 : -1;

    const moved =
      needX <= needY
        ? tryNudge(dirX * needX, 0) ||
          tryNudge(0, dirY * needY) ||
          tryNudge(-dirX * needX, 0) ||
          tryNudge(0, -dirY * needY) ||
          tryNudge(dirX * needX, dirY * needY)
        : tryNudge(0, dirY * needY) ||
          tryNudge(dirX * needX, 0) ||
          tryNudge(0, -dirY * needY) ||
          tryNudge(-dirX * needX, 0) ||
          tryNudge(dirX * needX, dirY * needY);

    if (!moved) {
      p.left = Math.min(bMaxX, saved.left + Math.abs(needX));
      p.top = Math.min(bMaxY, saved.top + Math.abs(needY));
    }
  }

  clampPt();
  return p;
}
